import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useProjects } from "@/contexts/ProjectContext";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import KpiCard from "@/components/dashboard/KpiCard";
import PageHeader from "@/components/dashboard/PageHeader";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, AreaChart, Area, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";

const PIE_COLORS = ["#C5A059", "#0A1128", "#9CAE96", "#D4B574"];

export default function SalesGrowthDashboard() {
  const { activeProjectId, activeProject } = useProjects();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!activeProjectId) return;
    api.get(`/sales/${activeProjectId}`).then(({ data }) => setData(data));
  }, [activeProjectId]);

  if (!data) return <DashboardLayout><p className="text-muted-foreground">Loading…</p></DashboardLayout>;

  const { totals, series, revenue_by_source, recommendations, top_landing_tests, terminology } = data;
  const sparkSignups = (series || []).slice(-14).map((s) => ({ value: s.signups || 0 }));
  const sparkOrders = (series || []).slice(-14).map((s) => ({ value: s.orders || 0 }));

  return (
    <DashboardLayout>
      <PageHeader
        overline="Revenue Growth"
        title="Where growth shows up in revenue."
        description={`Adapted to ${activeProject?.business_model || "your business model"} — ${terminology?.conversion_event || "conversions"}, revenue mix, and content source attribution.`}
      />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <KpiCard testId="sales-views" label={terminology?.primary_traffic_label || "Views"} value={(totals.views || 0).toLocaleString()} />
        <KpiCard testId="sales-orders" label={terminology?.top_kpi_a || "Orders"} value={(totals.orders || 0).toLocaleString()} sparkline={sparkOrders} sparkColor="#9CAE96" />
        <KpiCard testId="sales-signups" label={terminology?.signup_label || "Signups"} value={(totals.signups || 0).toLocaleString()} accent sparkline={sparkSignups} sparkColor="#C5A059" />
        <KpiCard testId="sales-rev-recurring" label={terminology?.revenue_recurring_label || "Recurring"} value={`$${(totals.revenue_recurring || 0).toLocaleString()}`} />
        <KpiCard testId="sales-rev-one-time" label={terminology?.revenue_one_time_label || "One-time"} value={`$${(totals.revenue_one_time || 0).toLocaleString()}`} />
        <KpiCard testId="sales-conversion" label="Conversion rate" value={`${totals.conversion_rate || 0}%`} sub="views → orders" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-6">
        <div className="lg:col-span-2 bg-white border border-border rounded-sm p-6">
          <p className="editorial-overline">Daily</p>
          <h3 className="font-serif text-xl mt-1 mb-4">{terminology?.primary_traffic_label || "Traffic"} (30d)</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series}>
                <defs>
                  <linearGradient id="navyArea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0A1128" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#0A1128" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#E5E0D5" vertical={false} />
                <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} fontSize={11} />
                <YAxis fontSize={11} />
                <Tooltip contentStyle={{ border: "1px solid #C5A059", borderRadius: 2 }} />
                <Area type="monotone" dataKey="views" stroke="#0A1128" strokeWidth={2} fill="url(#navyArea)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-border rounded-sm p-6">
          <p className="editorial-overline">Attribution</p>
          <h3 className="font-serif text-xl mt-1 mb-4">Revenue by content source</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={revenue_by_source}
                  dataKey="revenue"
                  nameKey="source"
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={86}
                  paddingAngle={2}
                  stroke="#FFFFFF"
                >
                  {revenue_by_source.map((entry) => (
                    <Cell key={entry.source} fill={PIE_COLORS[revenue_by_source.indexOf(entry) % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ border: "1px solid #C5A059", borderRadius: 2, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="bg-white border border-border rounded-sm p-6 mt-6">
        <p className="editorial-overline">By source — detail</p>
        <div className="h-64 mt-4">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={revenue_by_source} layout="vertical">
              <CartesianGrid stroke="#E5E0D5" horizontal={false} />
              <XAxis type="number" fontSize={11} />
              <YAxis dataKey="source" type="category" fontSize={11} width={130} />
              <Tooltip contentStyle={{ border: "1px solid #C5A059", borderRadius: 2 }} />
              <Bar dataKey="revenue" fill="#C5A059" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mt-6">
        <div className="bg-white border border-border rounded-sm p-6">
          <p className="editorial-overline">Recommendations for {activeProject?.business_model}</p>
          <ul className="mt-3 space-y-3">
            {recommendations.map((r) => (
              <li key={r} className="flex gap-3" data-testid={`sales-rec-${r.slice(0, 12)}`}>
                <span className="text-gold-500 font-serif text-lg leading-none mt-0.5">▸</span>
                <span className="text-sm text-navy-900 leading-relaxed">{r}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-white border border-border rounded-sm p-6">
          <p className="editorial-overline">Top landing tests</p>
          <ul className="mt-3 space-y-3">
            {top_landing_tests.map((r) => (
              <li key={r} className="text-sm text-navy-900/90 border-b border-border pb-3 last:border-0" data-testid={`landing-test-${r.slice(0, 12)}`}>{r}</li>
            ))}
          </ul>
        </div>
      </div>

      <div className="bg-white border border-border rounded-sm p-6 mt-6">
        <p className="editorial-overline">Abandoned checkouts</p>
        <p className="font-serif text-3xl mt-1">{totals.abandoned_checkouts}</p>
        <p className="text-sm text-muted-foreground mt-1">last 30d — opportunity to recover via 24-hour reminder + reactivation nudge.</p>
      </div>
    </DashboardLayout>
  );
}
